import {
  complianceDiretrixService,
  type DiretrixCaepfResponse,
  type DiretrixQuoteEngineDataResponse,
} from '@/services/compliance-diretrix';
import { validateCnpj } from '@/shared/utils';
import { removeFormatRegisterNumber } from '@/shared/utils/remove-format-register-number';
import { useDebouncedValue } from '@/shared/utils/use-filters.hook';
import { useQuery } from '@tanstack/react-query';
import { isValidCpfOrCaepf } from '../../utils/cpf-caepf-document.util';

export type DiretrixDocumentData =
  | ({ documentType: 'cnpj' } & DiretrixQuoteEngineDataResponse)
  | ({ documentType: 'cpfCaepf' } & DiretrixCaepfResponse);

type UseDiretrixDocumentDataParams = {
  documentNumber: string;
  useIndividualDocument: boolean;
  enabled?: boolean;
};

/**
 * Consulta cadastral por documento na Diretrix (via Compliance API) — fonte
 * única de CNPJ/CAEPF das cotações. `useIndividualDocument` alterna entre o
 * endpoint de CPF/CAEPF e o de CNPJ (`quote-engine-data`).
 */
export function useDiretrixDocumentData({
  documentNumber,
  useIndividualDocument,
  enabled = true,
}: UseDiretrixDocumentDataParams) {
  const cleanDocument = removeFormatRegisterNumber(documentNumber);
  const isCompleteDocument = useIndividualDocument
    ? isValidCpfOrCaepf(cleanDocument)
    : cleanDocument.length === 14 && validateCnpj(cleanDocument);
  const debouncedDocument = useDebouncedValue(
    enabled && isCompleteDocument ? cleanDocument : '',
    500
  );
  const documentType = useIndividualDocument ? 'cpfCaepf' : 'cnpj';

  const query = useQuery<DiretrixDocumentData, Error>({
    queryKey: ['diretrix', 'document-data', documentType, debouncedDocument],
    enabled: Boolean(enabled && debouncedDocument),
    retry: false,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (!debouncedDocument) {
        throw new Error('Documento incompleto');
      }

      if (useIndividualDocument) {
        const result = await complianceDiretrixService.getCaepfData({
          document: debouncedDocument,
        });

        if (!result.success || !result.data) {
          throw new Error(result.error ?? 'Falha ao consultar CPF/CAEPF');
        }

        return {
          documentType: 'cpfCaepf',
          ...result.data,
        };
      }

      const result = await complianceDiretrixService.getQuoteEngineData({
        cnpj: debouncedDocument,
      });

      if (!result.success || !result.data) {
        throw new Error(result.error ?? 'Falha ao consultar CNPJ');
      }

      return {
        documentType: 'cnpj',
        ...result.data,
      };
    },
  });

  return {
    ...query,
    cleanDocument,
    debouncedDocument,
    isCompleteDocument,
  };
}
